import React, {useEffect, useRef} from 'react';
import cx from 'classnames';
import Input from '../../../../../components/Input';
import Select from '../../../../../components/Select';
import Checkbox from '../../../../../components/Checkbox';
import {FormField} from '../../../../../types/ContactForm';
import {generateUniqueFieldName} from '../../../../../helpers/fieldNameGenerator';
import styles from './FieldConfiguration.module.scss';

interface FieldConfigurationProps {
    field: FormField;
    fieldIndex: number;
    allFields: FormField[];
    onUpdateField: (index: number, property: string, value: any) => void;
}

const FieldConfiguration: React.FC<FieldConfigurationProps> = ({
                                                                   field,
                                                                   fieldIndex,
                                                                   allFields,
                                                                   onUpdateField
                                                               }) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    
    useEffect(() => {
        if (field.label && field.label.trim() !== '') {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                const uniqueName = generateUniqueFieldName(field.label, allFields, fieldIndex);
                onUpdateField(fieldIndex, 'name', uniqueName);
            }, 100);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [field.label, allFields, fieldIndex]);

    const handleLabelChange = (property: string, value: string) => {
        onUpdateField(fieldIndex, property, value);
    };

    const fieldTypes = [
        {value: 'text', label: 'Text'},
        {value: 'email', label: 'E-Mail'},
        {value: 'url', label: 'URL'},
        {value: 'tel', label: 'Telefon'},
        {value: 'number', label: 'Zahl'},
        {value: 'textarea', label: 'Textbereich'},
        {value: 'select', label: 'Dropdown'},
        {value: 'checkbox', label: 'Checkbox'},
        {value: 'star-rating', label: 'Sterne-Bewertung'},
        {value: 'radio', label: 'Radio Buttons'},
        {value: 'acceptance', label: 'Zustimmung/Bestätigung'},
        {value: 'file', label: 'Dateiupload'}
    ];

    // Check if field is email type (including email-with-copy)
    const isEmailField = field.type === 'email' || field.type === 'email-with-copy';
    const isEmailCopyEnabled = field.type === 'email-with-copy';

    // Handle type change - convert email-with-copy to email when changing type
    const handleTypeChange = (property: string, value: string) => {
        // If changing from email-with-copy to non-email, just update to the new type
        // The email-with-copy will be converted automatically
        onUpdateField(fieldIndex, 'type', value);
    };

    // Handle email copy checkbox
    const handleEmailCopyChange = (checked: boolean) => {
        if (checked) {
            onUpdateField(fieldIndex, 'type', 'email-with-copy');
        } else {
            onUpdateField(fieldIndex, 'type', 'email');
        }
    };

    const widthOptions = [
        {value: '25', label: '25%'},
        {value: '33', label: '33%'},
        {value: '50', label: '50%'},
        {value: '66', label: '66%'},
        {value: '75', label: '75%'},
        {value: '100', label: '100%'}
    ];

    return (
        <div className={cx(styles['form-grid-wrapper'])}>
            <div className={cx(styles['form-grid'])}>
                <Input
                    type="text"
                    property="label"
                    value={field.label}
                    onChange={handleLabelChange}
                    placeholder="Feldbezeichnung"
                />

                <Select
                    property="type"
                    value={isEmailCopyEnabled ? 'email' : field.type}
                    onSelect={handleTypeChange}
                    placeholder="Feldtyp"
                    options={fieldTypes}
                    searchable={false}
                />

                <Select
                    property="width"
                    value={field.width?.toString() || '100'}
                    onSelect={(property, value) => onUpdateField(fieldIndex, property, parseInt(value))}
                    placeholder="Breite"
                    options={widthOptions}
                    searchable={false}
                />
                <div className={cx(styles['checkbox-container'])}>
                    <Checkbox
                        property="required"
                        value={field.required}
                        onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                        label="Pflichtfeld"
                    />
                </div>
            </div>

            <div className={cx(styles['form-grid-row'])}>
                {isEmailField && (
                    <div className={cx(styles['placeholder-container'])}>
                        <Input
                            type="text"
                            property="placeholder"
                            value={field.placeholder || ''}
                            onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                            placeholder="Platzhaltertext"
                        />
                        <div className={cx(styles['checkbox-container'])}>
                            <Checkbox
                                property="emailCopy"
                                value={isEmailCopyEnabled}
                                onChange={(property, value) => handleEmailCopyChange(value)}
                                label="Kopie an diese E-Mail-Adresse senden"
                            />
                        </div>
                    </div>
                )}

                {(['text', 'url', 'tel', 'number', 'textarea'].includes(field.type) || field.type === 'acceptance') && (
                    <div className={cx(styles['placeholder-container'])}>
                        <Input
                            type="text"
                            property="placeholder"
                            value={field.placeholder || ''}
                            onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                            placeholder={field.type === 'acceptance' ? 'Checkbox-Text (HTML erlaubt: <a href="...">Link</a>)' : 'Platzhaltertext'}
                        />
                        {field.type === 'acceptance' && (
                            <small className={cx(styles['help-text'])}>
                                Dieser Text wird als Checkbox-Label angezeigt. HTML-Tags wie &lt;a href="..."&gt; sind
                                erlaubt.
                            </small>
                        )}
                    </div>
                )}

                {field.type === 'number' && (
                    <div>
                        <div>
                            <Checkbox
                                property="useCommaFormat"
                                value={field.useCommaFormat || false}
                                onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                                label="Dezimalzahlen mit Komma (3,5)"
                            />

                            <Checkbox
                                property="showStepper"
                                value={field.showStepper || false}
                                onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                                label="Stepper-Buttons anzeigen"
                            />
                        </div>

                        <small className={cx(styles['help-text'])}>
                            Dezimalzahlen mit Komma: Erlaubt Dezimalzahlen mit Komma als Trennzeichen (z.B. 3,5).<br/>
                            Stepper-Buttons: Zeigt +/- Buttons zum Erhöhen/Verringern der Zahl.
                        </small>
                    </div>
                )}

                {field.type === 'file' && (
                    <div className={'zr-d-flex zr-gap-2 zr-flex-row zr-justify-between'}>
                        <div className={'zr-d-flex zr-flex-column zr-w-100'}>
                            <Input
                                type="number"
                                property="maxFileSize"
                                value={field.maxFileSize || 5}
                                onChange={(property, value) => onUpdateField(fieldIndex, property, parseInt(value) || 5)}
                                placeholder="Maximale Dateigröße (MB)"
                            />
                            <small>Maximale Dateigröße für Uploads in Megabyte</small>
                        </div>
                        <div className={'zr-d-flex zr-flex-column zr-w-100'}>
                            <Input type="text"
                                   property="allowedFileTypes"
                                   value={field.allowedFileTypes || ''}
                                   onChange={(property, value) => onUpdateField(fieldIndex, property, value)}
                                   placeholder="Erlaubte Dateitypen (z.B. .jpg,.png,.pdf)"
                            />
                            <small>Kommagetrennte Liste erlaubter Dateiendungen (z.B. .jpg,.png,.pdf). Leer lassen für
                                alle
                                Dateitypen.</small>
                        </div>
                    </div>

                )}
                {field.type === 'star-rating' && (
                    <div className={'zr-d-flex zr-flex-column'}>
                        <Input
                            type="number"
                            property="maxStars"
                            value={field.maxStars || 5}
                            onChange={(property, value) => onUpdateField(fieldIndex, property, parseInt(value) || 5)}
                            placeholder="Maximale Anzahl Sterne"
                        />
                        <small>Maximale Anzahl der Sterne für die Bewertung (Standard: 5)</small>
                    </div>
                )}

            </div>
        </div>
    );
};

export default FieldConfiguration; 